import { Css, ScrollableParent } from "@homebound/beam";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { NotFound } from "src/routes/NotFound";
import { GlobalOptionsPage } from "src/routes/libraries/global-options/GlobalOptionsPage";
import { PlanPackagePage } from "src/routes/libraries/plan-package/details/PlanPackagePage";
import { ProductOfferingConfigsPage } from "src/routes/libraries/product-offering-configs/ProductOfferingConfigsPage";
import { ScheduleTemplateDetailPage } from "src/routes/libraries/schedules/ScheduleTemplateDetail";
import { ScheduleTemplatesPage } from "src/routes/libraries/schedules/ScheduleTemplatesPage";
import { ProductOfferingCostCalculatorPage } from "../developments/product-offerings/ProductOfferingCostCalculatorPage";
import { ProductOfferingScopePage } from "../developments/product-offerings/ProductOfferingScopePage";
import {
  bidItemCreatePath,
  bidItemDetailsPath,
  catalogTaskFolderPath,
  catalogTaskLaborCosts,
  catalogTaskPath,
  catalogTasksPath,
  constraintItemsCatalogPath,
  designCatalogPath,
  designCatalogsPath,
  designPackageCompareModePath,
  designPackageEditOptionPath,
  designPackageFinishSchedulePath,
  designPackagePersonalizationGroupsPath,
  designPackageRequiredOptionsPath,
  designPackageVersionHistoryPath,
  globalOptionPath,
  globalOptionsLegacyPath,
  globalOptionsPath,
  globalProductOfferingConfigsPath,
  globalProductOfferingsPath,
  installTasksPath,
  itemCatalogPath,
  itemTemplatePath,
  itemTemplatesPath,
  librariesPath,
  materialCostPath,
  materialCreatePath,
  materialDetailsPath,
  materialsCatalogPath,
  milestoneCatalogPath,
  milestonesCatalogPath,
  planPackageEditPath,
  planPackageIncomingSlotReviewPath,
  planPackagePath,
  planPackageTakeoffsPath,
  planPackageVersionHistoryPath,
  planPackagesPath,
  productCatalogPath,
  productCreatePath,
  productDetailsPath,
  productOfferingConfigEditPath,
  productOfferingConfigPath,
  productOfferingConfigPricingTracePath,
  productOfferingCostCalculatorPath,
  productOfferingCreatePath,
  productOfferingEditPath,
  productOfferingScopePath,
  scheduleTemplatePath,
  scheduleTemplatesPath,
} from "../routesDef";
import { ItemTemplatePage } from "../settings/itemTemplates/ItemTemplatePage";
import { ItemTemplatesPage } from "../settings/itemTemplates/ItemTemplatesPage";
import { DesignCatalogsPage } from "./design-catalog/DesignCatalogsPage";
import { EditPersonalizationOptionPage } from "./design-catalog/EditPersonalizationOptionPage";
import { DesignPackageCompareModePage } from "./design-catalog/compare-mode/DesignPackageCompareMode";
import { DesignPackageConfiguratorLayout } from "./design-catalog/design-package-configurator/DesignPackageConfiguratorLayout";
import { DesignPackageRequiredOptionsPage } from "./design-catalog/design-package-configurator/components/DesignPackageRequiredOptionsPage";
import { FinishSchedulePage } from "./design-catalog/finish-schedules/FinishSchedulePage";
import { DesignPackagePersonalizationGroupsPage } from "./design-catalog/personalization-groups/DesignPackagePersonalizationGroupsPage";
import { DesignPackageVersionHistoryPage } from "./design-catalog/version-history/DesignPackageVersionHistoryPage";
import { GlobalOptionForm } from "./global-options-v2/GlobalOptionForm";
import { GlobalOptionsPageV2 } from "./global-options-v2/GlobalOptionsPageV2";
import { BidItemPage } from "./item-catalog/BidItemPage";
import { ItemCatalogPage } from "./item-catalog/ItemCatalogPage";
import { MaterialCatalogPage } from "./material-catalog/MaterialCatalogPage";
import { MaterialCostPage } from "./material-catalog/MaterialCostPage";
import { MaterialPage } from "./material-catalog/MaterialPage";
import { InstallTasksPage } from "./material-catalog/install-tasks/InstallTasksPage";
import { MilestoneCatalogForm } from "./milestone-catalog/MilestoneCatalogForm";
import { MilestoneCatalogPage } from "./milestone-catalog/MilestoneCatalogPage";
import { PlanPackagesPage } from "./plan-package/list/PlanPackagesPage";
import { CreateOrUpdatePlanPackageWizard } from "./plan-package/stepper";
import { PlanPackageTakeoffPage } from "./plan-package/takeoffs/PlanPackageTakeoffPage";
import { ReviewSlotRequestsPage } from "./plan-package/takeoffs/components/SlotReview/ReviewSlotRequestsPage";
import { PlanPackageVersionHistoryPage } from "./plan-package/version-history/PlanPackageVersionHistoryPage";
import { ProductCatalogPage } from "./product-catalog/ProductCatalogPage";
import { ProductPage } from "./product-catalog/ProductPage";
import { AddProductOfferingConfigPage } from "./product-offering-configs/AddProductOfferingConfigPage";
import { GlobalProductOfferings } from "./product-offerings/GlobalProductOfferings";
import { AddProductOfferingPage } from "./product-offerings/add-product-offering/AddProductOfferingPage";
import { CheckoutPricingTracePage } from "./product-offerings/checkout-pricing-trace/CheckoutPricingTracePage";
import { EditProductOfferingPage } from "./product-offerings/edit-product-offering/EditProductOfferingPage";
import { ConstraintItemCatalogPage } from "./task-catalog/ConstraintItemCatalogPage";
import { TaskCatalogLaborCostsPage } from "./task-catalog/TaskCatalogLaborCostsPage";
import { TaskCatalogPage } from "./task-catalog/TaskCatalogPage";
import { TaskCatalogForm } from "./task-catalog/components/TaskCatalogForm";

export function LibrariesLayout() {
  const hasOwnLayout = useRouteMatch([
    planPackageEditPath,
    planPackageIncomingSlotReviewPath,
    planPackageTakeoffsPath,
    productOfferingScopePath,
  ]);

  return hasOwnLayout ? (
    <LibraryRouter />
  ) : (
    <ScrollableParent px={3} xss={Css.fg1.bgGray100.$}>
      <LibraryRouter />
    </ScrollableParent>
  );
}

function LibraryRouter() {
  return (
    <Switch>
      <Route path={globalOptionsLegacyPath} exact component={GlobalOptionsPage} />
      <Route path={globalOptionsPath} exact component={GlobalOptionsPageV2} />
      <Route path={globalOptionPath} exact component={GlobalOptionForm} />
      <Route path={productCatalogPath} exact component={ProductCatalogPage} />
      <Route path={productDetailsPath} component={ProductCatalogPage} />
      <Route path={materialCreatePath} exact component={MaterialPage} />
      <Route path={materialCostPath} component={MaterialCostPage} />
      <Route path={materialDetailsPath} exact component={MaterialCatalogPage} />
      <Route path={installTasksPath} component={InstallTasksPage} />
      <Route path={materialsCatalogPath} component={MaterialCatalogPage} />
      <Route path={productCreatePath} exact component={ProductPage} />
      <Route path={itemCatalogPath} exact component={ItemCatalogPage} />
      <Route path={bidItemCreatePath} exact component={BidItemPage} />
      <Route path={bidItemDetailsPath} exact component={ItemCatalogPage} />
      <Route path={itemTemplatePath} component={ItemTemplatePage} />
      <Route path={scheduleTemplatePath.scheduleTemplate} exact component={ScheduleTemplateDetailPage} />
      <Route path={scheduleTemplatesPath} component={ScheduleTemplatesPage} />
      <Route path={itemTemplatesPath} exact component={ItemTemplatesPage} />
      <Route path={catalogTasksPath} exact component={TaskCatalogPage} />
      <Route path={catalogTaskPath} exact component={TaskCatalogForm} />
      <Route path={catalogTaskLaborCosts} exact component={TaskCatalogLaborCostsPage} />
      <Route path={constraintItemsCatalogPath} exact component={ConstraintItemCatalogPage} />
      <Route path={designCatalogsPath} exact component={DesignCatalogsPage} />
      <Route path={designPackageVersionHistoryPath} component={DesignPackageVersionHistoryPage} />
      <Route path={designPackagePersonalizationGroupsPath} component={DesignPackagePersonalizationGroupsPage} />
      <Route path={designPackageFinishSchedulePath} exact component={FinishSchedulePage} />
      <Route path={designPackageCompareModePath} exact component={DesignPackageCompareModePage} />
      <Route path={designPackageEditOptionPath} exact component={EditPersonalizationOptionPage} />
      <Route path={designPackageRequiredOptionsPath} exact component={DesignPackageRequiredOptionsPage} />
      <Route path={designCatalogPath} component={DesignPackageConfiguratorLayout} />
      <Route path={catalogTaskFolderPath} exact component={TaskCatalogPage} />
      <Route path={globalProductOfferingsPath} exact component={GlobalProductOfferings} />
      <Route path={productOfferingCreatePath} exact component={AddProductOfferingPage} />
      <Route path={productOfferingEditPath} exact component={EditProductOfferingPage} />
      <Route path={globalProductOfferingConfigsPath} exact component={ProductOfferingConfigsPage} />
      <Route path={productOfferingConfigPath} exact component={AddProductOfferingConfigPage} />
      <Route path={productOfferingConfigEditPath} exact component={AddProductOfferingConfigPage} />
      <Route path={productOfferingScopePath} exact component={ProductOfferingScopePage} />
      <Route path={productOfferingCostCalculatorPath} exact component={ProductOfferingCostCalculatorPage} />
      <Route path={productOfferingConfigPricingTracePath} exact component={CheckoutPricingTracePage} />
      <Route path={planPackageVersionHistoryPath} exact component={PlanPackageVersionHistoryPage} />
      <Route path={planPackageIncomingSlotReviewPath} component={ReviewSlotRequestsPage} />
      <Route path={planPackageTakeoffsPath} component={PlanPackageTakeoffPage} />
      <Route path={planPackagePath} component={PlanPackagePage} />
      <Route path={planPackagesPath} exact component={PlanPackagesPage} />
      <Route path={planPackageEditPath} exact component={CreateOrUpdatePlanPackageWizard} />
      <Route path={milestonesCatalogPath} exact component={MilestoneCatalogPage} />
      <Route path={milestoneCatalogPath} exact component={MilestoneCatalogForm} />
      <Route path={librariesPath} render={() => <Redirect to={globalOptionsPath} />} />
      {/* Otherwise, not found */}
      <Route component={NotFound} />
    </Switch>
  );
}
